import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Users, GraduationCap, BookOpen, DollarSign, Calendar, Bell,
  TrendingUp, TrendingDown, Clock, AlertCircle, CheckCircle,
  ArrowRight, CreditCard, FileText, UserCheck, CalendarDays, Building2,
  MessageCircle, Check
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SchoolAdminDashboard = () => {
  const { getAuthHeader, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [schoolInfo, setSchoolInfo] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [birthdayWishesSent, setBirthdayWishesSent] = useState({});
  const [stats, setStats] = useState({
    students: 0,
    teachers: 0,
    classes: 0,
    feesCollected: 0,
    feesPending: 0,
    todayAttendance: 0,
    attendancePercentage: 0,
    notices: 0
  });
  const [recentNotices, setRecentNotices] = useState([]);
  const [upcomingExams, setUpcomingExams] = useState([]);
  const [pendingFees, setPendingFees] = useState([]);
  const [todayBirthdays, setTodayBirthdays] = useState([]);

  // Load birthday wishes sent status from localStorage
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const savedWishes = localStorage.getItem(`birthdayWishes_${today}`);
    if (savedWishes) {
      setBirthdayWishesSent(JSON.parse(savedWishes));
    }
  }, []);

  // Send WhatsApp birthday wish
  const sendBirthdayWish = (student) => {
    const phone = student.father_mobile || student.mother_mobile || student.phone;
    if (!phone) {
      toast.error('No phone number available for this student');
      return;
    }
    
    // Clean phone number (remove +91, spaces, etc.)
    const cleanPhone = phone.replace(/\D/g, '');
    const formattedPhone = cleanPhone.startsWith('91') ? cleanPhone : `91${cleanPhone}`;
    
    // Birthday wish message
    const schoolName = schoolInfo?.name || 'Our School';
    const message = `🎂 *Happy Birthday ${student.name}!* 🎉

Wishing you a wonderful birthday filled with joy and happiness!

May this special day bring you endless smiles and beautiful memories.

Best wishes from,
*${schoolName}* Family 🏫`;

    // Open WhatsApp
    const whatsappUrl = `https://wa.me/${formattedPhone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    
    // Mark as sent
    const today = new Date().toISOString().split('T')[0];
    const newWishes = { ...birthdayWishesSent, [student.id]: true };
    setBirthdayWishesSent(newWishes);
    localStorage.setItem(`birthdayWishes_${today}`, JSON.stringify(newWishes));
    
    toast.success(`WhatsApp opened for ${student.name}'s birthday wish!`);
  };

  useEffect(() => {
    fetchAllData();
    
    // Update time every second
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // Fetch school info first
      const schoolRes = await axios.get(`${API_URL}/api/schools/current`, { headers: getAuthHeader() }).catch(() => ({ data: null }));
      setSchoolInfo(schoolRes.data);
      
      const [studentsRes, teachersRes, classesRes, analyticsRes, noticesRes, examsRes, attendanceRes, staffRes, dueFeesSummaryRes] = await Promise.all([
        axios.get(`${API_URL}/api/students?page=1&page_size=50`, { headers: getAuthHeader() }),  // Get paginated students
        axios.get(`${API_URL}/api/teachers`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/classes`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/analytics/dashboard`, { headers: getAuthHeader() }).catch(() => ({ data: { fees: { month_collected: 0, month_pending: 0 } } })),
        axios.get(`${API_URL}/api/notices`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/exams`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/attendance/today-summary`, { headers: getAuthHeader() }).catch(() => ({ data: { present: 0, total: 0 } })),
        axios.get(`${API_URL}/api/staff`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/fee-management/due-fees-summary`, { headers: getAuthHeader() }).catch(() => ({ data: { summary: {}, top_defaulters: [] } }))
      ]);

      const students = studentsRes.data?.items || studentsRes.data || [];
      const totalStudentsCount = studentsRes.data?.total || students.length;  // Use total from API
      const analyticsData = analyticsRes.data;
      const notices = noticesRes.data;
      const exams = examsRes.data;
      const attendance = attendanceRes.data;
      const dueFeesSummary = dueFeesSummaryRes.data;

      // Get fees from analytics dashboard (accurate calculated values)
      const totalCollected = analyticsData?.fees?.month_collected || 0;
      const totalPending = analyticsData?.fees?.total_pending || analyticsData?.fees?.month_pending || 0;

      // Get pending fees from due-fees-summary (top defaulters)
      const pendingFeesList = (dueFeesSummary?.top_defaulters || [])
        .slice(0, 5)
        .map(f => ({
          student_id: f.student_id,
          student_name: f.student_name,
          class_name: f.class,
          pending: f.total_due || 0,
          overdue: f.overdue || 0
        }));

      // Filter upcoming exams (next 7 days)
      const today = new Date();
      const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
      const upcoming = exams
        .filter(e => {
          const examDate = new Date(e.date);
          return examDate >= today && examDate <= nextWeek;
        })
        .slice(0, 5);

      // Find today's birthdays - Students + Teachers + Staff
      const todayStr = today.toISOString().slice(5, 10); // MM-DD format
      
      // Student birthdays
      const studentBirthdays = students.filter(s => {
        if (!s.dob) return false;
        const dobStr = s.dob.slice(5, 10);
        return dobStr === todayStr;
      }).map(s => ({ ...s, type: 'student' }));
      
      // Teacher birthdays
      const teachers = teachersRes.data?.items || teachersRes.data || [];
      const teacherBirthdays = teachers.filter(t => {
        if (!t.dob && !t.date_of_birth) return false;
        const dob = t.dob || t.date_of_birth;
        const dobStr = dob.slice(5, 10);
        return dobStr === todayStr;
      }).map(t => ({ ...t, type: 'teacher' }));
      
      // Staff birthdays
      const staffList = staffRes.data?.items || staffRes.data || [];
      const staffBirthdays = staffList.filter(s => {
        if (!s.dob && !s.date_of_birth) return false;
        const dob = s.dob || s.date_of_birth;
        const dobStr = dob.slice(5, 10);
        return dobStr === todayStr;
      }).map(s => ({ ...s, type: 'staff' }));
      
      // Combine all birthdays
      const allBirthdays = [...studentBirthdays, ...teacherBirthdays, ...staffBirthdays];

      // Calculate attendance percentage
      const attendancePercent = attendance.total > 0 
        ? Math.round((attendance.present / attendance.total) * 100) 
        : 0;

      // Handle paginated responses
      const classes = classesRes.data?.items || classesRes.data || [];

      setStats({
        students: totalStudentsCount,  // Use total from API, not array length
        teachers: Array.isArray(teachers) ? teachers.length : 0,
        classes: Array.isArray(classes) ? classes.length : 0,
        feesCollected: totalCollected,
        feesPending: totalPending,
        todayAttendance: attendance.present || 0,
        attendancePercentage: attendancePercent,
        notices: Array.isArray(notices) ? notices.length : 0
      });

      setRecentNotices(Array.isArray(notices) ? notices.slice(0, 4) : []);
      setUpcomingExams(upcoming);
      setPendingFees(pendingFeesList);
      setTodayBirthdays(allBirthdays);

    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { title: 'Total Students', value: stats.students, icon: Users, color: 'bg-blue-500', trend: '+5%' },
    { title: 'Total Teachers', value: stats.teachers, icon: GraduationCap, color: 'bg-purple-500' },
    { title: 'Total Classes', value: stats.classes, icon: BookOpen, color: 'bg-green-500' },
    { title: 'Fees Collected', value: `₹${stats.feesCollected.toLocaleString()}`, icon: DollarSign, color: 'bg-emerald-500', trend: '+12%' },
    { title: 'Fees Pending', value: `₹${stats.feesPending.toLocaleString()}`, icon: CreditCard, color: 'bg-amber-500' },
    { title: 'Active Notices', value: stats.notices, icon: Bell, color: 'bg-indigo-500' }
  ];

  return (
    <DashboardLayout title={schoolInfo?.name || "School Dashboard"}>
      <div className="space-y-6">
        {/* Date & Time Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
              <CalendarDays className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold">
                {currentTime.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
              <p className="text-muted-foreground flex items-center gap-2">
                <Clock className="h-4 w-4" />
                {currentTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </p>
            </div>
          </div>
          <div className="text-right hidden sm:block">
            <p className="text-sm text-muted-foreground">Welcome back,</p>
            <p className="font-semibold">{user?.name || 'Admin'}</p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="relative overflow-hidden" data-testid={`stat-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-xs font-medium text-muted-foreground">{stat.title}</CardTitle>
                    <div className={`${stat.color} p-2 rounded-lg`}>
                      <Icon className="h-3.5 w-3.5 text-white" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-xl font-bold">{stat.value}</div>
                    {stat.trend && (
                      <p className="text-xs text-green-600 flex items-center mt-1">
                        <TrendingUp className="h-3 w-3 mr-1" />
                        {stat.trend} from last month
                      </p>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Quick Overview Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          
          {/* Today's Attendance */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card data-testid="today-attendance-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <UserCheck className="h-4 w-4 text-primary" />
                    Today&apos;s Attendance
                  </CardTitle>
                  <Badge variant={stats.attendancePercentage >= 80 ? "default" : "destructive"}>
                    {stats.attendancePercentage}%
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <Progress value={stats.attendancePercentage} className="h-2" />
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      <CheckCircle className="h-3.5 w-3.5 inline mr-1 text-green-500" />
                      Present: {stats.todayAttendance}
                    </span>
                    <span className="text-muted-foreground">
                      <Users className="h-3.5 w-3.5 inline mr-1" />
                      Total: {stats.students}
                    </span>
                  </div>
                  <Button variant="outline" size="sm" className="w-full mt-2" onClick={() => navigate('/attendance')}>
                    <Calendar className="h-4 w-4 mr-2" />
                    View Attendance
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Pending Fees */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <Card data-testid="pending-fees-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-primary" />
                    Pending Fees
                  </CardTitle>
                  <Badge variant="outline">{pendingFees.length} students</Badge>
                </div>
              </CardHeader>
              <CardContent>
                {pendingFees.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No pending fees</p>
                ) : (
                  <div className="space-y-2">
                    {pendingFees.map((fee, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm p-2 bg-muted/50 rounded">
                        <span className="truncate flex-1">{fee.student_name || 'Student'}</span>
                        <span className="font-semibold text-amber-600">₹{fee.pending?.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
                <Button variant="outline" size="sm" className="w-full mt-3" onClick={() => navigate('/fee-management')}>
                  <DollarSign className="h-4 w-4 mr-2" />
                  Manage Fees
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* Upcoming Exams */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
            <Card data-testid="upcoming-exams-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="h-4 w-4 text-primary" />
                    Upcoming Exams
                  </CardTitle>
                  <Badge variant="secondary">{upcomingExams.length} this week</Badge>
                </div>
              </CardHeader>
              <CardContent>
                {upcomingExams.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No exams this week</p>
                ) : (
                  <div className="space-y-2">
                    {upcomingExams.map((exam, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm p-2 bg-muted/50 rounded">
                        <div className="flex-1">
                          <p className="font-medium truncate">{exam.subject || exam.name}</p>
                          <p className="text-xs text-muted-foreground">{exam.class_name}</p>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          <CalendarDays className="h-3 w-3 mr-1" />
                          {exam.date}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
                <Button variant="outline" size="sm" className="w-full mt-3" onClick={() => navigate('/exams')}>
                  <FileText className="h-4 w-4 mr-2" />
                  View All Exams
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* Recent Notices */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
            <Card data-testid="recent-notices-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Bell className="h-4 w-4 text-primary" />
                    Recent Notices
                  </CardTitle>
                  <Badge>{recentNotices.length}</Badge>
                </div>
              </CardHeader>
              <CardContent>
                {recentNotices.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No notices</p>
                ) : (
                  <div className="space-y-2">
                    {recentNotices.map((notice, idx) => (
                      <div key={idx} className="p-2 bg-muted/50 rounded">
                        <p className="font-medium text-sm truncate">{notice.title}</p>
                        <p className="text-xs text-muted-foreground flex items-center mt-1">
                          <Clock className="h-3 w-3 mr-1" />
                          {new Date(notice.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
                <Button variant="outline" size="sm" className="w-full mt-3" onClick={() => navigate('/notices')}>
                  <Bell className="h-4 w-4 mr-2" />
                  View All Notices
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* Today's Birthdays */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}>
            <Card data-testid="birthdays-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    🎂 Today&apos;s Birthdays
                  </CardTitle>
                  <Badge variant="secondary">{todayBirthdays.length}</Badge>
                </div>
              </CardHeader>
              <CardContent>
                {todayBirthdays.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No birthdays today</p>
                ) : (
                  <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                    {todayBirthdays.map((person, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-2 bg-muted/50 rounded">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0 ${
                          person.type === 'teacher' ? 'bg-gradient-to-br from-blue-400 to-indigo-500' :
                          person.type === 'staff' ? 'bg-gradient-to-br from-green-400 to-teal-500' :
                          'bg-gradient-to-br from-pink-400 to-purple-500'
                        }`}>
                          {person.name?.charAt(0) || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{person.name}</p>
                          <div className="flex items-center gap-1">
                            <Badge variant="outline" className={`text-[10px] px-1 py-0 ${
                              person.type === 'teacher' ? 'border-blue-300 text-blue-600' :
                              person.type === 'staff' ? 'border-green-300 text-green-600' :
                              'border-pink-300 text-pink-600'
                            }`}>
                              {person.type === 'teacher' ? 'Teacher' : 
                               person.type === 'staff' ? 'Staff' : 'Student'}
                            </Badge>
                            {person.type === 'student' && person.class_name && (
                              <span className="text-xs text-muted-foreground">{person.class_name}</span>
                            )}
                            {person.type === 'teacher' && person.subject && (
                              <span className="text-xs text-muted-foreground">{person.subject}</span>
                            )}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant={birthdayWishesSent[person.id] ? "secondary" : "default"}
                          className={`shrink-0 ${birthdayWishesSent[person.id] 
                            ? "bg-green-100 text-green-700 hover:bg-green-100 cursor-default" 
                            : "bg-green-600 hover:bg-green-700 text-white"}`}
                          onClick={() => !birthdayWishesSent[person.id] && sendBirthdayWish(person)}
                          disabled={birthdayWishesSent[person.id]}
                          data-testid={`whatsapp-btn-${person.id}`}
                        >
                          {birthdayWishesSent[person.id] ? (
                            <>
                              <Check className="h-3 w-3 mr-1" />
                              Sent
                            </>
                          ) : (
                            <>
                              <MessageCircle className="h-3 w-3 mr-1" />
                              WhatsApp
                            </>
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Quick Actions */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}>
            <Card data-testid="quick-actions-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/students')}>
                  <Users className="h-4 w-4 mr-2" />
                  Add New Student
                  <ArrowRight className="h-4 w-4 ml-auto" />
                </Button>
                <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/notices')}>
                  <Bell className="h-4 w-4 mr-2" />
                  Create Notice
                  <ArrowRight className="h-4 w-4 ml-auto" />
                </Button>
                <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/fee-management')}>
                  <DollarSign className="h-4 w-4 mr-2" />
                  Collect Fee
                  <ArrowRight className="h-4 w-4 ml-auto" />
                </Button>
                <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/reports')}>
                  <FileText className="h-4 w-4 mr-2" />
                  Generate Report
                  <ArrowRight className="h-4 w-4 ml-auto" />
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SchoolAdminDashboard;
